import { Controller } from "@hotwired/stimulus";

const DEFAULT_MODAL_ID = "app-modal-host";
const MODAL_CONTENT_ID = `${DEFAULT_MODAL_ID}-content`;
const MODAL_TITLE_ID = `${DEFAULT_MODAL_ID}-title`;
const MODAL_LOADING_TEMPLATE_ID = `${DEFAULT_MODAL_ID}-loading-template`;
const FULLSCREEN_DIALOG_CLASS = "app-modal-host--fullscreen";
const WIDE_DIALOG_CLASS = "app-modal-host--wide";

type HtmxAfterSwapEvent = Event & {
    detail?: {
        target?: EventTarget | null;
    };
};

type AppCloseModalEvent = Event & {
    detail?: {
        modalId?: string;
        value?: {
            modalId?: string;
        };
    };
};

type DialogElement = HTMLElement & {
    close?: () => void;
    hide?: () => void;
    show?: () => void;
    showModal?: () => void;
};

export default class extends Controller<HTMLElement> {
    private pendingModalId: string | null = null;
    private pendingSize: string | null = null;

    connect(): void {
        document.body.addEventListener("htmx:afterSwap", this.openPendingAfterSwap);
        document.addEventListener("app:close-modal", this.closeFromEvent);
        document.addEventListener("click", this.closeFromClick, true);
        document.addEventListener("close", this.resetFromNativeClose, true);
    }

    disconnect(): void {
        document.body.removeEventListener("htmx:afterSwap", this.openPendingAfterSwap);
        document.removeEventListener("app:close-modal", this.closeFromEvent);
        document.removeEventListener("click", this.closeFromClick, true);
        document.removeEventListener("close", this.resetFromNativeClose, true);
    }

    queueOpen(event: Event): void {
        const stimulusEvent = event as Event & { params?: { modalId?: string } };
        const currentTarget = event.currentTarget;
        this.pendingModalId =
            stimulusEvent.params?.modalId ||
            (currentTarget instanceof HTMLElement ? currentTarget.dataset.adminDialogModalId : "") ||
            DEFAULT_MODAL_ID;
        this.pendingSize = currentTarget instanceof HTMLElement ? currentTarget.dataset.adminDialogSize || "" : "";
        this.applySize(this.pendingModalId, this.pendingSize);

        if (
            currentTarget instanceof HTMLElement &&
            currentTarget.dataset.adminDialogOpenImmediately === "true"
        ) {
            this.renderLoadingState(this.pendingModalId);
            this.syncLabel(this.pendingModalId);
            this.showDialog(this.pendingModalId);
        }
    }

    private openPendingAfterSwap = (event: Event): void => {
        const target = (event as HtmxAfterSwapEvent).detail?.target;

        if (!this.pendingModalId || !(target instanceof HTMLElement)) {
            return;
        }

        if (target.id !== MODAL_CONTENT_ID) {
            return;
        }

        const modalId = this.pendingModalId;
        const requestedSize = this.readContentSize() || this.pendingSize || "";
        this.pendingModalId = null;
        this.pendingSize = null;
        this.applySize(modalId, requestedSize);
        this.syncLabel(modalId);
        this.showDialog(modalId);
    };

    private closeFromEvent = (event: Event): void => {
        const detail = (event as AppCloseModalEvent).detail || {};
        const modalId = detail.modalId || detail.value?.modalId || DEFAULT_MODAL_ID;

        this.closeDialog(modalId);
        this.syncLabel(modalId);
        this.resetSize(modalId);
    };

    private closeFromClick = (event: Event): void => {
        const dialog = event.target;
        if (dialog instanceof HTMLDialogElement && dialog.id === DEFAULT_MODAL_ID &&
            dialog.querySelector('[data-admin-dialog-dismiss-outside="true"]') && event instanceof MouseEvent) {
            const bounds = dialog.getBoundingClientRect();
            if (event.clientX < bounds.left || event.clientX > bounds.right ||
                event.clientY < bounds.top || event.clientY > bounds.bottom) {
                this.closeDialog(dialog.id);
                this.resetSize(dialog.id);
                return;
            }
        }
        const trigger =
            event.target instanceof Element
                ? event.target.closest("[data-admin-dialog-close]")
                : null;

        if (!(trigger instanceof HTMLElement)) {
            return;
        }

        event.preventDefault();
        const modalId = trigger.dataset.adminDialogModalId || DEFAULT_MODAL_ID;
        this.closeDialog(modalId);
        this.syncLabel(modalId);
        this.resetSize(modalId);
    };

    private resetFromNativeClose = (event: Event): void => {
        const dialog = event.target;

        if (dialog instanceof HTMLElement && dialog.id === DEFAULT_MODAL_ID) {
            this.resetSize(dialog.id);
        }
    };

    private showDialog(modalId: string): void {
        const dialog = document.getElementById(modalId) as DialogElement | null;

        if (!(dialog instanceof HTMLElement) || dialog.hasAttribute("open")) {
            return;
        }

        try {
            if (typeof dialog.showModal === "function") {
                dialog.showModal();
                return;
            }

            if (typeof dialog.show === "function") {
                dialog.show();
                return;
            }
        } catch (_error) {
            if (typeof dialog.show === "function") {
                dialog.show();
                return;
            }
        }

        dialog.setAttribute("open", "");
    }

    private closeDialog(modalId: string): void {
        const dialog = document.getElementById(modalId) as DialogElement | null;

        if (!(dialog instanceof HTMLElement)) {
            return;
        }

        if (typeof dialog.close === "function") {
            dialog.close();
            return;
        }

        if (typeof dialog.hide === "function") {
            dialog.hide();
            return;
        }

        dialog.removeAttribute("open");
    }

    private syncLabel(modalId: string): void {
        const dialog = document.getElementById(modalId);
        const title = document.getElementById(MODAL_TITLE_ID);
        const label = title?.textContent ? title.textContent.trim() : "";

        if (!(dialog instanceof HTMLElement)) {
            return;
        }

        if (label) {
            dialog.setAttribute("label", label);
            dialog.setAttribute("aria-label", label);
        } else {
            dialog.setAttribute("label", "");
            dialog.removeAttribute("aria-label");
        }

        if (title instanceof HTMLElement) {
            dialog.setAttribute("aria-labelledby", title.id);
        } else {
            dialog.removeAttribute("aria-labelledby");
        }
    }

    private readContentSize(): string {
        const content = document.getElementById(MODAL_CONTENT_ID);

        return content instanceof HTMLElement
            ? content.querySelector<HTMLElement>("[data-admin-dialog-size]")?.dataset.adminDialogSize || ""
            : "";
    }

    private renderLoadingState(modalId: string): void {
        const content = document.getElementById(`${modalId}-content`);
        const template = document.getElementById(MODAL_LOADING_TEMPLATE_ID);

        if (!(content instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) {
            return;
        }

        content.replaceChildren(template.content.cloneNode(true));
    }

    private applySize(modalId: string, requestedSize: string): void {
        const dialog = document.getElementById(modalId);

        if (!(dialog instanceof HTMLElement)) {
            return;
        }

        dialog.classList.toggle(FULLSCREEN_DIALOG_CLASS, requestedSize === "fullscreen");
        dialog.classList.toggle(WIDE_DIALOG_CLASS, requestedSize === "wide");
    }

    private resetSize(modalId: string): void {
        const dialog = document.getElementById(modalId);

        if (dialog instanceof HTMLElement) {
            dialog.classList.remove(FULLSCREEN_DIALOG_CLASS, WIDE_DIALOG_CLASS);
        }
    }
}
